import React from 'react';
import './TermsPage.scss';

interface TermsPageProps {
  menuOpen: boolean;
}

const TermsPage: React.FC<TermsPageProps> = ({ menuOpen }) => {
  return (
    <div className={`terms-page ${menuOpen ? 'menu-open' : ''}`}>
      <div className="terms-page__hero">
        <div className="terms-page__content">
          <h1 className="terms-page__title">TERMS & CONDITIONS</h1>
          
          <div className="terms-page__text-content">
            <p className="terms-page__updated">Last Updated: May 2026</p>

            <section className="terms-page__section">
              <h2>1. Introduction</h2>
              <p>
                These Terms and Conditions ("Terms") govern your use of Stage Door Weddings' wedding videography services. By booking our services, you agree to be bound by these Terms.
              </p>
            </section>

            <section className="terms-page__section">
              <h2>2. Booking and Payment</h2>
              <h3>2.1 Booking Confirmation</h3>
              <p>
                A booking is confirmed only when we receive a signed contract and the deposit payment. We reserve the right to decline any booking at our discretion.
              </p>
              
              <h3>2.2 Deposit</h3>
              <p>
                A non-refundable deposit of 30% of the total package price is required to secure your wedding date. The remaining balance is due 14 days before your wedding date.
              </p>
              
              <h3>2.3 Payment Methods</h3>
              <p>
                We accept payment by bank transfer, credit/debit card, or cheque. All prices are in GBP and include VAT where applicable.
              </p>
              
              <h3>2.4 Late Payment</h3>
              <p>
                If the final balance is not received 14 days before your wedding, we reserve the right to cancel the booking and retain the deposit.
              </p>
            </section>

            <section className="terms-page__section">
              <h2>3. Cancellation and Refunds</h2>
              <h3>3.1 Cancellation by Client</h3>
              <p>
                If you cancel your booking, the following cancellation fees apply:
              </p>
              <ul>
                <li>More than 6 months before the wedding: Deposit retained</li>
                <li>3-6 months before the wedding: 50% of total package price</li>
                <li>Less than 3 months before the wedding: 100% of total package price</li>
              </ul>
              
              <h3>3.2 Cancellation by Stage Door Weddings</h3>
              <p>
                In the unlikely event that we need to cancel, we will provide a full refund of all payments made and assist in finding alternative videography services.
              </p>
              
              <h3>3.3 Postponement</h3>
              <p>
                If you need to postpone your wedding, we will do our best to accommodate your new date subject to availability. One postponement is permitted without charge; subsequent changes may incur an administrative fee.
              </p>
            </section>

            <section className="terms-page__section">
              <h2>4. Our Services</h2>
              <h3>4.1 Coverage Hours</h3>
              <p>
                Coverage hours are as specified in your chosen package. Additional hours can be arranged at our standard hourly rate, subject to availability.
              </p>
              
              <h3>4.2 Filming Locations</h3>
              <p>
                Our services include filming at locations specified in your booking. Travel costs for venues more than 30 miles from our base may incur additional charges.
              </p>
              
              <h3>4.3 Equipment and Backup</h3>
              <p>
                We use professional-grade equipment and maintain backup equipment for all weddings. However, we cannot be held liable for equipment failure beyond our control.
              </p>
            </section>

            <section className="terms-page__section">
              <h2>5. Delivery and Revisions</h2>
              <h3>5.1 Delivery Timeline</h3>
              <p>
                Your edited wedding film will be delivered within 12 weeks of your wedding date. During peak season (May-September), this may extend to 16 weeks.
              </p>
              
              <h3>5.2 Revisions</h3>
              <p>
                The number of revisions included in your package is specified in your contract. Additional revisions may be requested at an additional cost.
              </p>
              
              <h3>5.3 Delivery Format</h3>
              <p>
                Your wedding film will be delivered via a private online gallery. USB drives and other physical media can be provided at an additional cost.
              </p>
            </section>

            <section className="terms-page__section">
              <h2>6. Copyright and Usage Rights</h2>
              <h3>6.1 Copyright Ownership</h3>
              <p>
                Stage Door Weddings retains the copyright to all footage and edited films. You are granted a personal, non-commercial licence to use the final film.
              </p>
              
              <h3>6.2 Portfolio and Marketing</h3>
              <p>
                We reserve the right to use excerpts from your wedding film for portfolio, marketing, and promotional purposes unless you specifically opt out in writing.
              </p>
              
              <h3>6.3 Music Licensing</h3>
              <p>
                We use licensed music for your wedding film. You may not use the film for commercial purposes or public broadcast without additional licensing.
              </p>
            </section>

            <section className="terms-page__section">
              <h2>7. Liability and Force Majeure</h2>
              <h3>7.1 Limitation of Liability</h3>
              <p>
                Our liability is limited to a refund of fees paid. We are not liable for any indirect, consequential, or special damages.
              </p>
              
              <h3>7.2 Force Majeure</h3>
              <p>
                We are not liable for failure to perform our obligations due to circumstances beyond our control, including but not limited to natural disasters, illness, accidents, or government restrictions.
              </p>
              
              <h3>7.3 Backup Videographer</h3>
              <p>
                In the event of illness or emergency, we will provide a qualified replacement videographer of similar experience and skill level.
              </p>
            </section>

            <section className="terms-page__section">
              <h2>8. Client Responsibilities</h2>
              <h3>8.1 Venue Permissions</h3>
              <p>
                You are responsible for obtaining necessary permissions from your venue for videography. Some venues have restrictions on filming during ceremonies.
              </p>
              
              <h3>8.2 Timeline and Schedule</h3>
              <p>
                Please provide us with a detailed timeline of your wedding day at least 2 weeks before the event. Significant deviations from the agreed schedule may affect coverage.
              </p>
              
              <h3>8.3 Cooperation</h3>
              <p>
                We require cooperation from you, your wedding party, and other vendors to perform our services effectively. We are not responsible for missed shots due to lack of cooperation.
              </p>
            </section>

            <section className="terms-page__section">
              <h2>9. Data Protection</h2>
              <p>
                We process your personal data in accordance with our Privacy Policy and applicable data protection laws. Your data will be used solely for providing our services and will not be shared with third parties without your consent.
              </p>
            </section>

            <section className="terms-page__section">
              <h2>10. Complaints and Disputes</h2>
              <p>
                If you have any concerns about our services, please contact us immediately at bellkeir@gmail.com. We aim to resolve all complaints amicably within 14 days.
              </p>
              <p>
                These Terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </section>

            <section className="terms-page__section">
              <h2>11. Changes to Terms</h2>
              <p>
                We reserve the right to update these Terms at any time. Changes will be posted on this page with an updated revision date. Your continued use of our services constitutes acceptance of any changes.
              </p>
            </section>

            <section className="terms-page__section">
              <h2>12. Contact Information</h2>
              <p>
                For questions about these Terms and Conditions, please contact us at:
              </p>
              <ul>
                <li>Email: bellkeir@gmail.com</li>
                <li>Through our contact form on this website</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
